import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate,useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import MainMenu from '../MainMenu';
import api from '../api/api';
import {marked} from 'marked';
import parse from 'html-react-parser';
import StockPriceGraph from './StockPriceGraph';
import ComparingGraph from '../dashboard/components/ComparingGraph';

const HEIGHT_THRESHOLD = 750; // ページあたりの最大高さ
const FIG_HEIGHT = 200; // 図がある場合の追加高さ

const getMonthName = (month) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[month - 1];
};

// ページのヘッダー
const header = (summaryData) => {
    const year = summaryData.date.slice(0, 4);
    const month = summaryData.date.slice(5, 7);
    const day = parseInt(summaryData.date.slice(8, 10));
    const monthName = getMonthName(parseInt(month));

    return (
        <>
            <div className="product-name">Insight Note</div>
            <div className="title">
                <div className="left"></div>
                <div className="main-title center">DAILY REPORT</div>
                <div className="right">POWERED BY<br /><span className="space-with-small-device"></span>AI & LLM</div>
                <div className="date">
                    <div className="month-day">{`${monthName}. ${day}`}</div>
                    <div className="year">{year}</div>
                </div>
            </div>
            <div className="company-name">{summaryData.company} ({summaryData.sec_code})</div>
        </>
    );
};

// ページのフッター
const footer = () => (
    <div className="footer">
        <div className="disclaimer">
            本レポートは投資助言を目的としたものではなく、あくまで情報提供のみを目的としています。また、本レポートは生成AIを用いて作成されています。データに基づき作成していますが、完全性を保証するものではありません。重要な意思決定は必ず確認するようにしてください。
        </div>
    </div>
);

// ページのコンテンツを生成
const createJsx = (articleContent, stockData, financialData) => {
    const jsxContents = [];
    for (let i = 0; i < articleContent.length; i++) {
        const content = articleContent[i];

        // 'stock-fig'が含まれているか確認してグラフを表示
        if (content.includes('stock-fig')) {
            const rechartGraph = <StockPriceGraph stockPrice={stockData} />;
            jsxContents.push(rechartGraph);
        } else if (content.includes('fig_sales')) {
            const rechartGraph = <ComparingGraph financialData={financialData} title="" column="sales" selectedPeriod={'annual'} className="daily-report-fig"/>;
            jsxContents.push(rechartGraph);
        } else if (content.includes('fig_operating_income')) {
            const rechartGraph = <ComparingGraph financialData={financialData} title="" column="operating_income" selectedPeriod={'annual'} className="daily-report-fig"/>;
            jsxContents.push(rechartGraph);
        } else if (content.includes('fig_net_income')) {
            const rechartGraph = <ComparingGraph financialData={financialData} title="" column="net_income" selectedPeriod={'annual'} className="daily-report-fig"/>;
            jsxContents.push(rechartGraph);
        } else {
            jsxContents.push(parse(content));
        }
    }
    return jsxContents;
};

const createPage = (summaryData, pageData, isFirstPage, articleContent, stockData, financialData) => {
    return (
        <div id="large-container" key={pageData.pageNo}>
            <div className="report-container">
                {header(summaryData)}
                {isFirstPage && <div className="summary">{summaryData.contents}</div>}
                <div className={`article-container ${isFirstPage ? "article-container-first" : ""}`}>
                    {createJsx(articleContent, stockData, financialData)}
                </div>
                {footer()}
            </div>
        </div>
    );
};

const DailyReport = () => {
    const { secCode, date } = useParams();
    const [dailyReport, setDailyReport] = useState([]);
    const [pages, setPages] = useState([]);
    const draftContainerRef = useRef(null);
    const [showDraftContainer, setShowDraftContainer] = useState(true);
    const [stockData, setStockData] = useState(null);
    const [draftContent, setDraftContent] = useState([]);
    const [draftFinished, setDraftFinished] = useState(false);
    const [financialData, setFinancialData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchFinancialResults = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/financial-results/${secCode}/2`);
                const reversedData = [...response.data].reverse()
                setFinancialData(reversedData);
            } catch (error) {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    navigate('/login', { state: { from: location.pathname } });
                } else {
                    console.error('Error fetching financial results:', error);
                }
            }
        };
    
        const fetchDailyReport = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/daily-report/${secCode}/${date}`);
                setDailyReport(response.data);
                // financial_results_summaryが存在する場合のみfetchFinancialResultsを呼び出す
                const hasFinancialResultsSummary = response.data.find((section) => section.section == 'financial_results_summary');
                if (hasFinancialResultsSummary) {
                    await fetchFinancialResults(); // 順序を保証するためにawaitを使用
                } else {
                    setFinancialData({}); // 空のデータでもセット
                }
            } catch (error) {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    navigate('/login', { state: { from: location.pathname } });
                } else {
                    console.error('Error fetching financial results:', error);
                }
            }
        };
    
        const fetchStockData = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/stock-price-time-series/${secCode}/${date}`);
                setStockData(response.data);
            } catch (error) {
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    navigate('/login', { state: { from: location.pathname } });
                } else {
                    console.error('Error fetching stock data:', error);
                }
            }
        };
    
        // データの取得を開始
        fetchDailyReport();
        fetchStockData();
    }, [secCode, date]);
    

    useEffect(() => {
        if (dailyReport.length > 0 && stockData && financialData !== null) {
            const draft = createDraft(dailyReport, stockData); // Draft を生成
            setDraftContent(draft); // Draft Content を保存
            setDraftFinished(true); // Draft 完成をトリガー
        }
    }, [dailyReport, stockData, financialData]);
    
        // ドラフトの作成    
    const createDraft = (report) => {
        if (report.length > 0) {
            const reportContents = []
            // 株価サマリ
            const stockSummary = report.find((r) => r.section == "stock_summary");
            if (stockSummary) {
                let reportContent = (
                    <> 
                        <h2><i className="fa-solid fa-arrow-trend-up"></i>株価</h2>
                        <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(stockSummary.contents) }}></p>
                    </>
                )
                reportContents.push(reportContent);
                // 株価グラフ
                reportContents.push(<div className="stock-fig"></div>);
            }

            // 株価変動要因
            const stockVariationReason = report.find((r) => r.section == "stock_variation_reason");
            if (stockVariationReason) {
                const reportContent = (
                    <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(stockVariationReason.contents) }}></p>
                )
                reportContents.push(reportContent);
            }
            
            // 決算情報
            const financialResultsSummary= report.find((r) => r.section == "financial_results_summary");
            if (financialResultsSummary) {
                let reportContent = (
                    <>
                        <h2><i class="fa-solid fa-chart-simple"></i>決算情報</h2>
                        <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(financialResultsSummary.contents) }}></p>
                    </>
                )
                reportContents.push(reportContent);
            }
            
            // 決算情報詳細
            const financialResultsDetail= report.find((r) => r.section == "financial_results_detail");
            if (financialResultsDetail) {
                const modifiedContent = financialResultsDetail.contents.replace('```markdown', '').replace('```', '')
                let reportContent = (
                    <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(modifiedContent)}}></p>
                )
                reportContents.push(reportContent);
            }
            
            // 適時開示
            const timelyDisclosureSummary = report.find((r) => r.section == "timely_disclosure_summary");
            if (timelyDisclosureSummary) {
                let reportContent = (
                    <>
                        <h2><i class="fa-regular fa-file-lines"></i>適時開示</h2>
                        <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(timelyDisclosureSummary.contents) }}></p>
                    </>
                )
                reportContents.push(reportContent);
            }
            
            // ニュース
            const newsSummary = report.find((r) => r.section == "news_summary");
            if (newsSummary) {
                let reportContent = (
                    <>
                        <h2><i class="fa-regular fa-newspaper"></i>ニュース</h2>
                        <p className="paragraph" dangerouslySetInnerHTML={{ __html: marked(newsSummary.contents) }}></p>
                    </>
                )
                reportContents.push(reportContent);
            }
            return reportContents
        }
    }

    const paginateContent = (summaryData) => {
        const draftContainer = draftContainerRef.current;
        
        let totalHeight = 0;
        let currentPageNo = 1;
        let articleContent = [];
        let newPages = [];
        if (!draftContainer ||  draftContainerRef.current.childElementCount === 0) {
            newPages.push(createPage(summaryData, { pageNo: currentPageNo, contents: '' }, currentPageNo === 1, articleContent, stockData, financialData));
            setPages(newPages);
            setShowDraftContainer(false);
            return;
        }
    
        const isLargeDisplay = window.innerWidth >= 1000;
        const children = Array.from(draftContainer.children);
        children.forEach((child, index) => {
            // 高さのしきい値を設定
            const heightThreshold = isLargeDisplay
                ? (HEIGHT_THRESHOLD - (currentPageNo === 1 ? 50 : 0)) * 2
                : HEIGHT_THRESHOLD;
    
            // child が "paragraph" クラスの場合は再帰的に処理
            if (child.classList.contains("paragraph")) {
                const paragraphChildren = Array.from(child.children); // paragraph 内の子要素を取得
                paragraphChildren.forEach((paragraphChild, pIndex) => {
                    const paragraphChildHtml = paragraphChild.outerHTML;
                    const paragraphChildHeight = paragraphChild.offsetHeight;
    
                    totalHeight += paragraphChildHeight;

                    if (paragraphChildHtml.includes('fig')) {
                        totalHeight += FIG_HEIGHT;
                    }
                    
                    // console.log(paragraphChild)
                    // console.log(heightThreshold, paragraphChildHeight, totalHeight)
                    if (totalHeight > heightThreshold) {
                        // ページが溢れたら新しいページを作成
                        newPages.push(createPage(summaryData, { pageNo: currentPageNo, contents: '' }, currentPageNo === 1, articleContent, stockData, financialData));
                        currentPageNo++;
                        totalHeight = paragraphChildHeight; // 新しいページに現在の要素の高さをリセット
                        articleContent = [paragraphChildHtml]; // 新しいページにこの子要素を追加
                    } else {
                        articleContent.push(paragraphChildHtml); // 現在のページに子要素を追加
                    }
                });
            } else {
                // 通常の要素の処理
                const childHtml = child.outerHTML;
                const childHeight = child.offsetHeight;

                totalHeight += childHeight;

                if (child.outerHTML.includes('fig')) {
                    totalHeight += FIG_HEIGHT;
                }
                // console.log(child)
                // console.log(heightThreshold, childHeight, totalHeight)
                if (totalHeight > heightThreshold) {
                    // ページが溢れたら新しいページを作成
                    newPages.push(createPage(summaryData, { pageNo: currentPageNo, contents: '' }, currentPageNo === 1, articleContent, stockData, financialData));
                    currentPageNo++;
                    totalHeight = childHeight;
                    articleContent = [childHtml]; // 新しいページに子要素を移動
                } else {
                    articleContent.push(childHtml); // そのまま現在のページに追加
                }
            }
        });
    
        // 最後のページも追加
        if (articleContent.length > 0) {
            newPages.push(createPage(summaryData, { pageNo: currentPageNo, contents: '' }, currentPageNo === 1, articleContent, stockData, financialData));
        }
        setPages(newPages);
        setShowDraftContainer(false);
    };
    
    useEffect(() => {
        const summaryData = dailyReport.find(data => data.section==="summary")
        if (draftFinished && summaryData) {
            paginateContent(summaryData);
        }
    }, [draftFinished]);

    if (!draftFinished) {
        return <div>Loading ...</div>
    }

    return (
        <div className='daily-report-body'>
            <MainMenu />
            <div className="main-container">
                <Sidebar selectedIcon="daily-report" />
            </div>
            <div className="report-display">
            {pages.map((page, index) => (
                <div key={index}>
                    {page}
                </div>
            ))}

            { showDraftContainer ? 
                <div id="draft-container" ref={draftContainerRef}>
                    {draftContent}
                </div> : null
            }
            </div>
        </div>
    );
};

export default DailyReport;
